import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import { AgaveDataGrid } from '@/components/GenericTable/DataGridStyled';
import DetailsModal from '@/components/Modals/DetailsModal/DetailsModal';
import DownloadModal from '@/components/Modals/DownloadModal/DownloadModal';
import SearchInput from '@/components/SearchInput';

import ScheduleModal from './dependencies/ScheduleModal/ScheduleModal';
import { pickupColumns } from './pickups-columns';
import usePickups from './usePickups';

const PickupSection = () => {
  const hook = usePickups();
  const theme = useTheme();
  const { t } = useTranslation('pickup');

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          padding: `${theme.spacing(4)} 0 0`,
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap="1rem"
          width="100%"
          sx={{ padding: `0 ${theme.spacing(4)}` }}
        >
          <SearchInput
            buttonText={t('BUTTONS.SEARCH.LABEL')}
            placeholder={t('BUTTONS.SEARCH.PLACEHOLDER')}
            handleKeyPress={hook.handleKeyPress}
            onChange={hook.onChgSearch}
            onClick={hook.onSearch}
          />
          <Typography
            sx={{ color: theme.palette.grey[600], fontWeight: theme.typography.fontWeightMedium }}
          >
            {t('SUBTITLE')}
          </Typography>
        </Box>
        <Box width="100%" mt={2}>
          {hook.trackingIds.length > 0 && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                backgroundColor: 'rgb(248, 246, 244)',
                padding: '1rem 1rem 1rem 2%',
              }}
            >
              <Box display="flex" alignItems="center" gap="1rem">
                <Typography width="max-content">
                  <strong>
                    {hook.trackingIds.length} {`orden`}
                    {hook.trackingIds.length === 1
                      ? t('TABLE.SELECT_ONE')
                      : t('TABLE.SELECT_MULTIPLE')}
                  </strong>
                  (máximo 10)
                </Typography>
                <Button onClick={hook.handleOpenScheduleModal} size="medium">
                  {t('BUTTONS.SCHEDULE')}
                </Button>
              </Box>
              <Button variant="contained" onClick={() => hook.handleOpenDownloadModal()}>
                {t('BUTTONS.DOWNLOAD')}
              </Button>
            </Box>
          )}
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Box sx={{ minWidth: '700px' }}>
              <AgaveDataGrid
                columns={pickupColumns(hook.handleOpenDownloadModal)}
                rows={hook.shipments.data || []}
                localeText={{
                  noRowsLabel: 'No hay envíos disponibles',
                }}
                rowCount={
                  hook.shipments.totalPages
                    ? hook.shipments.totalPages * (hook.pagination.filters.page_size as number)
                    : 0
                }
                loading={hook.shipments.loading}
                checkboxSelection={true}
                disableRowSelectionOnClick={true}
                onCellClick={(params) => {
                  if (!['actions', '__check__'].includes(params.colDef.field)) {
                    hook.fetchShipmentsByTrackingId(params.row.trackingId);
                  }
                }}
                getRowId={(row) => row.trackingId}
                hideFooterSelectedRowCount={true}
                paginationModel={{
                  page: hook.pagination.filters.page as number,
                  pageSize: hook.pagination.filters.page_size as number,
                }}
                disableColumnMenu={true}
                paginationMode="server"
                onPaginationModelChange={hook.pagination.onPaginationModelChange}
                isRowSelectable={(params) =>
                  (!(hook.trackingIds.length + 1 > 10) ||
                    hook.trackingIds.includes(params.row.trackingId)) &&
                  !hook.deliveriesTypesNotAvailable.includes(params.row.deliveryType)
                }
                rowSelectionModel={{ ids: new Set(hook.trackingIds), type: 'include' }}
                onRowSelectionModelChange={(selectionModel) => {
                  const selectedArray = Array.from(selectionModel.ids) as string[];
                  if (selectedArray.length > 10) {
                    const limitedSelection = selectedArray.slice(0, 10);
                    hook.selectRows(limitedSelection);
                  } else {
                    hook.selectRows(selectedArray);
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      <DownloadModal
        isOpen={hook.downloadModal}
        onClose={hook.handleCloseDownloadModal}
        downloadLabels={hook.downloadLabels}
        trackingIds={hook.singleTrackingId ? [hook.singleTrackingId] : hook.trackingIds}
      />

      <DetailsModal
        isOpen={hook.detailModal}
        onClose={hook.handleCloseDetailModal}
        orderDetails={hook.shipmentDetails.data}
        map={false}
        isPickups={true}
        onOrderCancelled={() => hook.fetchShipments({ search: hook.search })}
      />

      <ScheduleModal
        isOpen={hook.scheduleModal}
        onClose={hook.handleCloseScheduleModal}
        selected={hook.trackingIds}
        fetchShipments={hook.fetchShipments}
        orders={hook.shipments.data}
      />
    </>
  );
};

export default PickupSection;
